import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellType, Column, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'

import { BaseComponent, BlueStakesLog, FeatureType, Grid, ListItem, ModalSize, VpJob, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BlueStakesLogComponent } from './blue-stakes-log.component'

@Component({
    selector: 'app-blue-stakes-logs',
    templateUrl: './blue-stakes-logs.component.html',
    styleUrls: ['./blue-stakes-logs.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridColumnGroupComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
    ],
})
export class BlueStakesLogsComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)

    grid: Grid<BlueStakesLog>

    jobMap: ListItem<string>[] = []
    jobDisplayMap = new DataMap([], 'value', 'display')
    vistaUserMap = new DataMap([], 'id', 'name')

    activeJobs: VpJob[] = []
    activeVistaUsers: VpUser[] = []

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsBlueStakesLogEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<BlueStakesLog>(this.services)
            .setupModalEditing(
                this.services.data.blueStakesLogs,
                'Blue Stakes Log',
                BlueStakesLogComponent,
                () => new BlueStakesLog(),
                { component: this },
                ModalSize.large,
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => {
                return [
                    item.job,
                    this.jobDisplayForItem(item),
                    item.address,
                    item.ticketNumber,
                    item.phoneNumber,
                    item.notes,
                    BlueStakesLogsComponent.formatRequestTime(item.requestTime),
                    this.vistaUserMap.getDisplayValue(item.requesterHrRef),
                    this.formatOwnerNames(item),
                ]
            })

        this.grid.modified.on(this.refreshItems.bind(this))
        this.refresh()
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshItems)
    }

    refreshItems() {
        const sub = this.services.data
            .load(true, true, this.services.data.blueStakesLogs.queryAll())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.grid.items = result[0] as BlueStakesLog[]
                },
            })
        this.cancelableRequest('', true, sub)
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.blueStakesLogs.queryAll(),
                this.services.data.get<VpJob[]>('api/VpJob'),
                this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.grid.items = result[0] as BlueStakesLog[]
                    this.activeJobs = result[1] as VpJob[]
                    this.buildJobMap()
                    this.vistaUserMap = new DataMap(result[2] as VpUser[], 'id', 'name')
                    this.activeVistaUsers = result[3] as VpUser[]
                    this.initialLoadingComplete()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    buildJobMap() {
        const jobs = this.activeJobs
            .map(job => new ListItem<string>(job.id, job.id + ' ' + job.name))
            .sort((a, b) => a.display.localeCompare(b.display))
        this.jobMap = jobs.length > 0 ? [new ListItem<string>('', 'Select a Job'), ...jobs] : []

        // Same as pending-change-orders: map job id → `id + ' ' + name` for the grid column DataMap.
        const jobDisplayEntries = this.activeJobs.map(j => ({
            value: j.id,
            display: j.id + ' ' + j.name,
        }))
        this.jobDisplayMap = new DataMap(jobDisplayEntries, 'value', 'display')
    }

    /** Grid + search: full job label when `item.job` matches VpJob id (or a loose id match). */
    private jobDisplayForItem(item: BlueStakesLog): string {
        const raw = item.job
        if (raw == null) return ''
        const s = String(raw)
        let display = this.jobDisplayMap.getDisplayValue(s) ?? this.jobDisplayMap.getDisplayValue(s.trim())
        if (display != null && String(display).length > 0) return String(display)
        const canonical = BlueStakesLogsComponent.resolveCanonicalJobId(s, this.activeJobs)
        if (canonical != null) {
            display = this.jobDisplayMap.getDisplayValue(canonical)
            if (display != null && String(display).length > 0) return String(display)
        }
        return s
    }

    /** Compare job ids loosely (trim, ignore trailing dots, case). */
    static normalizeJobIdForCompare(id: string): string {
        return id.trim().replace(/\.+$/g, '').toLowerCase()
    }

    /**
     * Returns the Vista list's canonical `VpJob.id` for a value persisted on a log row, or `null` if unknown.
     * Use when opening the modal so `[(ngModel)]="target.job"` matches `[selectedValuePath]="'value'"` on the combo.
     */
    static resolveCanonicalJobId(stored: string | undefined | null, jobs: VpJob[]): string | null {
        if (stored == null) return null
        const raw = String(stored).trim()
        if (!raw) return null
        const want = BlueStakesLogsComponent.normalizeJobIdForCompare(raw)
        for (const j of jobs) {
            if (!j?.id) continue
            if (BlueStakesLogsComponent.normalizeJobIdForCompare(j.id) === want) {
                return j.id
            }
        }
        return null
    }

    formatOwnerNames(item: BlueStakesLog): string {
        const refs = item.ticketOwnerHrRefs
        if (!refs?.length) return ''
        return refs
            .map(id => this.vistaUserMap.getDisplayValue(id) ?? String(id))
            .filter(Boolean)
            .join(' / ')
    }

    static formatRequestTime(value: unknown): string {
        if (value == null || value === '') return ''
        const parts = String(value).split(':')
        if (parts.length >= 2) {
            let h = parseInt(parts[0], 10)
            const m = parseInt(parts[1], 10)
            if (!Number.isNaN(h) && !Number.isNaN(m)) {
                h = ((h % 24) + 24) % 24
                const mm = m.toString().padStart(2, '0')
                const hour12 = h % 12 === 0 ? 12 : h % 12
                const suffix = h < 12 ? 'AM' : 'PM'
                return `${hour12}:${mm} ${suffix}`
            }
        }
        return String(value)
    }

    mainGridItemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLElement) => {
        if (panel.cellType !== CellType.Cell) return
        const binding = panel.grid.columns[c].binding as string | null
        const row = panel.grid.rows[r]
        const item = row?.dataItem as BlueStakesLog | undefined
        if (!item || !binding) return

        cell.style.textDecoration = item.isActive ? '' : 'line-through'

        if (binding === 'isActive') {
            cell.classList.toggle('bsl-active-cell', item.isActive)
            cell.classList.toggle('bsl-inactive-cell', !item.isActive)
            cell.style.backgroundColor = item.isActive ? '#d1e7dd' : '#f8d7da'
            const checkbox = cell.querySelector('input[type="checkbox"]') as HTMLInputElement | null
            if (checkbox) {
                checkbox.style.accentColor = item.isActive ? '#198754' : '#dc3545'
            }
        } else {
            cell.classList.remove('bsl-active-cell', 'bsl-inactive-cell')
            cell.style.backgroundColor = ''
        }

        if (binding === 'job') {
            cell.textContent = this.jobDisplayForItem(item)
        } else if (binding === 'requestTime') {
            cell.textContent = BlueStakesLogsComponent.formatRequestTime(item.requestTime)
        } else if (binding === 'ticketOwnerHrRefs') {
            cell.textContent = this.formatOwnerNames(item)
        }
    }

    onActiveCellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        const col = flexGrid.columns[args.col] as Column
        if (col.binding !== 'isActive') return

        const item = flexGrid.rows[args.row].dataItem as BlueStakesLog
        this.services.data.blueStakesLogs.edit(item).subscribe({
            next: () => flexGrid.invalidate(),
            error: () => {
                item.isActive = !item.isActive
                flexGrid.invalidate()
                this.services.notification.error('The Blue Stakes change was not saved')
            },
        })
        flexGrid.invalidate()
    }
}
